import { defineStore } from 'pinia'
import { baseApi } from '~/api/base'
import { getRuntimeAuthKeys } from '~/composables/useStorageKey'
import { useUserStore } from '~/store/common/user'
import { buildCategoryTreeFromApi } from '~/utils/store/categoryTree'

function parseJwtPayload(token) {
  try {
    const [, payload] = String(token || '').split('.')
    if (!payload) return null
    if (typeof atob === 'function') {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
      return JSON.parse(atob(normalized))
    }
    if (typeof Buffer !== 'undefined') {
      return JSON.parse(Buffer.from(payload, 'base64url').toString('utf8'))
    }
    return null
  } catch {
    return null
  }
}

function resolveCategoryAuthKey() {
  let token = ''
  try {
    const userStore = useUserStore()
    token = userStore.accessToken || ''
  } catch {}

  try {
    const keys = getRuntimeAuthKeys()
    const tokenCookie = useCookie(keys.accessTokenCookie, { readonly: true })
    token = token || tokenCookie.value || ''
  } catch {}

  if (!token) return 'guest'

  const payload = parseJwtPayload(token)
  return `token:${payload?.cloud_code || payload?.sub || token.slice(-16)}`
}

/**
 * @description 商品分类 store：拉取 `/prod/prod/getCategoryProd` 后在全局归一化为 categories + children 双结构
 * @reason 新接口使用 children 嵌套 + grade；业务侧统一读 categoryList（一级）及其 categories（二级）
 */
export const useCategoryStore = defineStore('category', {
  state: () => ({
    /** @type {Array<Object>} 归一化后的一级分类树 */
    categoryTree: [],
    /** 是否已成功请求过（含空结果） */
    fetched: false,
    /** 上次分类树对应的登录身份，避免复用游客分类给已登录用户 */
    fetchedAuthKey: '',
    /** 是否已经在浏览器端按当前登录态成功拉取过，避免复用 SSR 阶段的游客/远端缓存 */
    fetchedClient: false,
    /** 加载状态 */
    loading: false,
  }),

  getters: {
    /**
     * @description 过滤后的一级分类列表（含二级 categories、完整 children）
     * @param {Object} state
     * @returns {Array<Object>}
     */
    categoryList: (state) => (Array.isArray(state.categoryTree) ? state.categoryTree : []),

    /**
     * @description 分类是否可用（已成功拉取且至少有一级分类）
     * @param {Object} state
     * @returns {boolean}
     */
    hasCategories: (state) =>
      Boolean(state.fetched) &&
      (Array.isArray(state.categoryTree) ? state.categoryTree.length : 0) > 0,

    /**
     * @description 分类 ID 索引，供页面和导航从 store O(1) 取当前分类信息
     * @param {Object} state
     * @returns {Map<string, Object>}
     */
    categoryById: (state) => {
      const map = new Map()
      const walk = (nodes) => {
        if (!Array.isArray(nodes)) return
        nodes.forEach((node) => {
          const categoryId = String(node?.categoryId ?? '').trim()
          if (categoryId) {
            map.set(categoryId, node)
          }
          walk(node?.categories || node?.children || [])
        })
      }

      walk(Array.isArray(state.categoryTree) ? state.categoryTree : [])
      return map
    },
  },

  actions: {
    /**
     * @description 确保 categoryTree 始终为数组（兼容旧版 state / 异常 hydration）
     * @returns {Array<Object>}
     */
    ensureCategoryTreeState() {
      if (!Array.isArray(this.categoryTree)) {
        this.categoryTree = []
      }
      return this.categoryTree
    },

    /**
     * @description 拉取商品分类并在 store 内归一化
     * @param {{ force?: boolean }} [options]
     * @returns {Promise<Array<Object>>}
     */
    async fetchCategoryInfo({ force = false, requestConfig = {}, authKey: requestedAuthKey } = {}) {
      this.ensureCategoryTreeState()

      const authKey = requestedAuthKey || resolveCategoryAuthKey()

      if (this._fetchPromise) {
        if (this._fetchAuthKey === authKey) {
          return this._fetchPromise
        }
      }

      const canUseFetchedCache =
        this.fetched &&
        this.fetchedAuthKey === authKey &&
        (!import.meta.client || this.fetchedClient)
      if (!force && canUseFetchedCache) {
        return this.categoryTree
      }

      this.loading = true
      this._fetchAuthKey = authKey
      const requestAuthKey = authKey
      const retainedState = {
        categoryTree: this.categoryTree,
        fetched: this.fetched,
        fetchedAuthKey: this.fetchedAuthKey,
        fetchedClient: this.fetchedClient,
      }
      const canRetainCurrentIdentitySnapshot =
        retainedState.fetched && retainedState.fetchedAuthKey === requestAuthKey
      const requestPromise = (async () => {
        try {
          const data = await baseApi.getCategoryProdTree(requestConfig)
          const tree = buildCategoryTreeFromApi(data)
          if (this._fetchAuthKey === requestAuthKey) {
            this.categoryTree = Array.isArray(tree) ? tree : []
            this.fetched = true
            this.fetchedAuthKey = requestAuthKey
            this.fetchedClient = import.meta.client
          }
          return this.categoryTree
        } catch (error) {
          if (this._fetchAuthKey === requestAuthKey) {
            this.categoryTree = canRetainCurrentIdentitySnapshot ? retainedState.categoryTree : []
            this.fetched = canRetainCurrentIdentitySnapshot ? retainedState.fetched : false
            this.fetchedAuthKey = canRetainCurrentIdentitySnapshot
              ? retainedState.fetchedAuthKey
              : ''
            this.fetchedClient = canRetainCurrentIdentitySnapshot
              ? retainedState.fetchedClient
              : false
          }
          throw error
        } finally {
          if (this._fetchPromise === requestPromise) {
            this.loading = false
            this._fetchPromise = null
            this._fetchAuthKey = ''
          }
        }
      })()
      this._fetchPromise = requestPromise

      return this._fetchPromise
    },

    /**
     * @description 按 categoryId 查找分类节点（含各级子分类）
     * @param {string|number} categoryId
     * @returns {Object|null}
     */
    findCategoryById(categoryId) {
      const target = String(categoryId ?? '').trim()
      if (!target) return null
      return this.categoryById.get(target) || null
    },
  },
})
